import { Controller } from "@hotwired/stimulus"

/**
 * Send Button Controller
 *
 * Manages the send button state for the assistant.
 * - On form submit: immediately switches to processing mode (pulse + gray background)
 * - On processingValue change to false (via broadcast): restores to send mode (blue background)
 * - When a submission ends without a response (network failure): restores to send mode
 * - During processing: button is disabled, but textarea remains enabled for typing
 *
 * Usage:
 * <button data-controller="send-button"
 *         data-send-button-processing-value="false">
 *   <svg>...</svg>
 * </button>
 */
export default class extends Controller {
  static values = {
    processing: { type: Boolean, default: false },
    processingLabel: { type: String, default: 'processing' }
  }

  connect() {
    this.form = this.element.closest('form')

    if (this.form) {
      this._boundOnSubmit = this._onSubmit.bind(this)
      this._boundOnSubmitEnd = this._onSubmitEnd.bind(this)
      this.form.addEventListener('submit', this._boundOnSubmit)
      this.form.addEventListener('turbo:submit-end', this._boundOnSubmitEnd)
    }

    // Apply initial state based on value from server
    if (this.processingValue) {
      this._applyProcessingState()
    }
  }

  disconnect() {
    if (this.form && this._boundOnSubmit) {
      this.form.removeEventListener('submit', this._boundOnSubmit)
      this.form.removeEventListener('turbo:submit-end', this._boundOnSubmitEnd)
    }
  }

  // Called when processing value changes (e.g., from Turbo Stream broadcast)
  processingValueChanged() {
    if (this.processingValue) {
      this._applyProcessingState()
    } else {
      this._applyNormalState()
    }
  }

  // --- Private ---

  _onSubmit() {
    // Immediately switch to processing state when form is submitted
    this.processingValue = true
  }

  _onSubmitEnd(event) {
    // The server answered, so it owns the button state from here on: it either
    // re-renders the button with the conversation's real state (even on a
    // rejection) or broadcasts it once the answer completes.
    if (event.detail.fetchResponse) return

    // No response at all (network failure, aborted request): nothing will ever
    // replace this button, so restore it or the user cannot retry.
    this.processingValue = false
  }

  _applyProcessingState() {
    // Disable the button - it's just a visual indicator during processing
    this.element.disabled = true
    this.element.type = 'button'
    this.element.setAttribute('aria-label', this.processingLabelValue)
    // Switch to in-progress state
    this.element.classList.remove('icon-ready')
    this.element.classList.add('icon-in-progress')
    // Textarea stays enabled so users can write their next message
  }

  _applyNormalState() {
    this.element.disabled = false
    this.element.type = 'submit'
    this.element.setAttribute('aria-label', 'send')
    // Switch to ready state
    this.element.classList.remove('icon-in-progress')
    this.element.classList.add('icon-ready')
  }
}
